<script>
import { mapGetters } from "vuex";
import QrcodeVue from "qrcode.vue";

export default {
  name: "MenuQrCard",
  components: { QrcodeVue },

  props: {
    lastEventTimeStamp: {
      type: [String, Number, Date],
      required: false,
    },
    startDate: {
      type: [String, Date],
      required: true,
    },
    endDate: {
      type: [String, Date],
      required: true,
    },
  },

  computed: {
    ...mapGetters("menu", ["menuCampaign"]),

    formattedStartDate() {
      return this.formatDate(this.startDate);
    },
    formattedEndDate() {
      return this.formatDate(this.endDate);
    },
    formattedLastEvent() {
      return this.lastEventTimeStamp
        ? this.formatDateTime(this.lastEventTimeStamp)
        : "Aucune activité";
    },
  },

  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    formatDateTime(date) {
      if (!date) return "";
      const [day, month, yearTime] = date.split("/");
      const [year, time] = yearTime.split(" ");
      const isoString = `${year}-${month}-${day}T${time}`;
      return new Date(isoString).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    openMenu() {
      window.open(this.menuCampaign.urlCode, "_blank");
    },
    goToSettings() {
      this.$router.push("/settings/menu");
    },
    goToOrders() {
      this.$router.push("/order/history");
    },
    goToStats() {
      this.$router.push("/data");
    },
  },
};
</script>

<template>
  <div class="menu-glass-card">
    <!-- QR -->
    <div class="qr-glass px-2 py-3">
      <qrcode-vue
        :value="menuCampaign.urlCode"
        :size="140"
        level="H"
        render-as="svg"
      />
      <div class="qr-brand">DIGITABLE</div>
    </div>

    <!-- CONTENT -->
    <div class="menu-content px-2 py-3">
      <div class="menu-header">
        <h3>{{ menuCampaign.menuName }}</h3>
        <span class="menu-badge">MENU DIGITAL</span>
      </div>

      <!-- META INFO -->
      <div class="meta-info my-3">
        <div class="meta-item">
          <span class="meta-label">Période</span>
          <span class="meta-value">
            {{ formattedStartDate }} → {{ formattedEndDate }}
          </span>
        </div>

        <div class="meta-item">
          <span class="meta-label">Dernière activité</span>
          <span class="meta-value highlight">
            {{ formattedLastEvent }}
          </span>
        </div>
      </div>

      <!-- ACTIONS -->
      <div class="actions">
        <button class="btn-primary text-uppercase" @click="openMenu">
          Visualiser le menu
        </button>

        <div class="quick-actions">
          <button @click="goToSettings" title="Paramétrage">
            <img class="align-middle" alt="setting" width="20px" src="@/assets/icons/setting-s.svg" />
          </button>
          <button @click="goToOrders" title="Commandes">
            <img class="align-middle" alt="recipt" width="20px" src="@/assets/icons/recipt.svg" />
          </button>
          <button @click="goToStats" title="Données collectées">            
            <img class="align-middle" alt="data-service" width="20px" src="@/assets/icons/data-service.svg" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.menu-glass-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 26px;
  background: linear-gradient(
    145deg,
    rgba(255, 255, 255, 0.9),
    rgba(245, 246, 250, 0.85)
  );
  border-radius: 20px;
  backdrop-filter: blur(10px);
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

/* QR code */
.qr-glass {
  min-width: 180px;
  text-align: center;
  padding: 10px;
  border-radius: 16px;
}

.qr-brand {
  margin-top: 12px;
  font-size: 12px;
  letter-spacing: 2px;
  font-weight: 700;
  color: #adb5bd;
}

/* Content */
.menu-content {
  flex: 1;
}

.menu-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.menu-header h3 {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #212529;
}

.menu-badge {
  background: linear-gradient(135deg, #ffd36e, #D4AF37);
  color: #fff;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}

/* META INFO */
.meta-info {
  display: flex;
  gap: 22px;
  margin-bottom: 22px;
}

.meta-item {
  background: #f8f9fa;
  padding: 10px 14px;
  border-radius: 12px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
}

.meta-label {
  display: block;
  font-size: 11px;
  color: #adb5bd;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.meta-value {
  font-size: 14px;
  font-weight: 600;
  color: #212529;
}

.meta-value.highlight {
  color: #D4AF37;
}

/* ACTIONS */
.actions {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.btn-primary {
  background: linear-gradient(135deg, #f4b740, #D4AF37);
  border: none;
  color: #fff;
  padding: 12px 22px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(212, 175, 55, 0.4);
  transition: all 0.3s ease;
  letter-spacing: 1.2px;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(212, 175, 55, 0.55);
}

.quick-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.quick-actions button {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  font-size: 16px;
  background: #f1f3f5;
  box-shadow:
    inset 0 0 0 1px rgba(0, 0, 0, 0.05),
    0 6px 14px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.quick-actions button:hover {
  background: #D4AF37;
  color: #fff;
  transform: translateY(-2px);
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .menu-glass-card {
    flex-direction: column;
    text-align: center;
    align-items: center;
  }

  .menu-header {
    justify-content: center;
  }

  .meta-info {
    flex-direction: column;
    width: 100%;
  }

  .actions {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }

  .quick-actions {
    justify-content: center;
  }

  .qr-glass {
    margin-bottom: 16px;
  }
}
</style>
